import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import ServiceCard from '../../components/ServiceCard';
import RegistrationPopup from '../../components/RegistrationPopup';
import MessagePopup from '../../components/MessagePopup';
import ServicePopup from '../../components/ServicePopup';
import ServiceLogsPopup from '../../components/ServiceLogsPopup';
import { postRequest } from '../../utils/networking'; 
import { apiUrls } from '../../utils/url';

export default function ServicesSection() {
  const [isMessagePopupOpen, setIsMessagePopupOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isRegistrationPopupOpen, setIsRegistrationPopupOpen] = useState(false);
  const [isServiceLogsOpen, setIsServiceLogsOpen] = useState(false);
  const [logData, setLogData] = useState([]);
  const [currentUserName, setCurrentUserName] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [messageFormData, setMessageFormData] = useState({name: '',email: '',phonenumber: '',question: '' });

  const serviceModalRef = useRef(null);
  const registrationPopupRef = useRef(null);
  const messagePopupRef = useRef(null);

  const servicesData = [
    {
      id: 0,
      image: "https://i.pinimg.com/1200x/de/c3/a6/dec3a6cb51913d7876702cb0bc56e14d.jpg",
      title: "Seller Account Setup",
      description: "We help you register your Amazon, Flipkart & Meesho seller accounts without confusion or rejection. From documents to shipping settings — we make your account ready to start selling.",
      serviceType: "Seller Account Setup",
      serviceTypeId: 0,
      price: "4,000 / month"
    },
    {
      id: 1,
      image: "https://i.pinimg.com/1200x/39/e0/ce/39e0ce6711236aeb93825ec19cf69550.jpg",
      title: "GST Registration & Filing",
      description: "We guide you on how to apply for GST registration required for marketplace selling (Note: We do not apply on your behalf). Giving lifetime support and GST related queries.",
      serviceType: "GST Registration & Filing",
      serviceTypeId: 1,
      price: "1,500"
    },
    {
      id: 2,
      image: "https://i.pinimg.com/1200x/9e/29/70/9e2970fa9c828505137596d8beca6d6a.jpg",
      title: "E-Commerce Website",
      description: "We help you build your own professional online store from scratch. Launch your brand website and start selling directly to customers.",
      serviceType: "E-Commerce Website",
      serviceTypeId: 2,
      price: "7,000"
    },
    {
      id: 3,
      image: "https://i.pinimg.com/1200x/73/d2/38/73d238c7051d7cf111a2899d1d970932.jpg",
      title: "Product Listing Creation",
      description: "We create SEO-friendly product listings that attract more visibility and clicks. Better presentation means higher chances of converting visitors into buyers.",
      serviceType: "Product Listing Creation",
      serviceTypeId: 3,
      price: "100"
    },
    {
      id: 5,
      image: "https://i.pinimg.com/1200x/a4/74/7a/a4747a9d6ba4a858a620fd2c10fda2b9.jpg",
      title: "Trademark Registration",
      description: "We guide you through the complete trademark registration process for your brand. Secure your brand name and make it eligible for Amazon Brand Registry.",
      serviceType: "Trademark Registration",
      serviceTypeId: 5,
      price: "11,000"
    },
    {
      id: 6,
      image: "https://i.pinimg.com/736x/a3/76/94/a3769400e8d72ddb11fba219b7913124.jpg",
      title: "Seller Account Auditing",
      description: "We help you launch your product with a structured strategy. Gain early visibility and improve your chances of getting initial sales faster.",
      serviceType: "Seller Account Auditing",
      serviceTypeId: 6,
      price: "2,000"
    },
    {
      id: 7,
      image: "https://i.pinimg.com/1200x/73/d2/38/73d238c7051d7cf111a2899d1d970932.jpg",
      title: "Image Creation",
      description: "Get high-quality product images to boost your listings and attract more buyers.",
      serviceType: "Image Creation",
      serviceTypeId: 7,
      price: "500"
    }
  ];

  const serviceDetails = {
    0: {
      title: 'Seller Account Setup – Required Details',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Details needed for Open Seller Account:</h3>
            <ul className="list-disc pl-5 text-gray-600 space-y-1">
              <li>Mobile Number</li>
              <li>Email Id: (in business name and fresh email id preferred)</li>
              <li>GST Details</li>
              <li>Bank Account Details (Current Bank Acc preferred)</li>
            </ul>
          </div>
        </div>
      )
    },
    1: {
      title: 'GST Registration – Documents Required',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Documents Required For GST:</h3>
            <ul className="list-disc pl-5 text-gray-600 space-y-1">
              <li>Pan Card copy</li>
              <li>Aadhar copy</li>
              <li>Passport size photo</li>
              <li>Business Name</li>
              <li>Mobile Number</li>
              <li>Email id</li>
              <li>Nature of business</li>
              <li>EB Receipt/ Property Tax</li>
              <li>Rental agreement if rented house</li>
            </ul>
          </div>
        </div>
      )
    },
    2: {
      title: 'E-Commerce Website Creation – Required Details',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Details needed for E-Commerce Website:</h3>
            <ul className="list-disc pl-5 text-gray-600 space-y-1">
              <li>Business Name</li>
              <li>Product Categories</li>
              <li>Logo and Brand Colors</li>
              <li>Domain Name Preference</li>
              <li>Hosting Requirements</li>
              <li>Payment Gateway Preferences</li>
              <li>Shipping Integration Needs</li>
            </ul>
          </div>
        </div>
      )
    },
    3: {
      title: 'Product Listing Creation – Requirements',
      content: (
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">📸 Product Image Requirements</h3>
            <p className="text-gray-600 mb-2">To create high-quality listing images for your product, please share the following:</p>

            <h4 className="font-semibold text-gray-800 mt-3">1 Image with Pure White Background</h4>
            <p className="text-gray-600 text-sm ml-2">(Front-facing product image on plain white background)</p>
            <p className="text-gray-600 text-sm ml-2 mb-2">Image clearly shows product dimensions (L,B,H)</p>

            <h4 className="font-semibold text-gray-800 mt-3">1 Close-Up Image</h4>
            <p className="text-gray-600 text-sm ml-2">(Zoomed-in shot highlighting product details, texture or finish)</p>

            <h4 className="font-semibold text-gray-800 mt-3">All Angles Image</h4>
            <p className="text-gray-600 text-sm ml-2">(Click images of the product from all angles to show depth and shape of the product)</p>

            <h4 className="font-semibold text-gray-800 mt-3">1 Lifestyle Image (if available)</h4>
            <p className="text-gray-600 text-sm ml-2">(Product in real-life usage or setting)</p>

            <h4 className="font-semibold text-gray-800 mt-3">Brand Logo File</h4>
            <p className="text-gray-600 text-sm ml-2">(PNG format preferred)</p>

            <h4 className="font-semibold text-gray-800 mt-3">Packaging Image (if applicable)</h4>

            <div className="mt-4 p-3 bg-amber-50 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-1">Please ensure:</h4>
              <ul className="list-disc pl-5 text-gray-600 text-sm">
                <li>✔ Product is clearly visible</li>
                <li>✔ Images are in good lighting</li>
                <li>✔ No blur or shadow</li>
                <li>✔ High resolution (minimum 2000px recommended)</li>
              </ul>
              <p className="text-gray-600 text-sm mt-2">This helps us create optimized images suitable for Amazon & Flipkart listings.</p>
            </div>
          </div>
        </div>
      )
    },
    5: {
      title: 'Trademark Registration – Documents Required',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Documents required for Trademark:</h3>
            <ul className="list-disc pl-5 text-gray-600 space-y-1">
              <li>Trade name</li>
              <li>Logo</li>
              <li>Aadhar card</li>
              <li>Pan card</li>
              <li>Mail id</li>
              <li>Mobile number</li>
              <li>Nature of business</li>
              <li>Place of business proof</li>
            </ul>
          </div>
        </div>
      )
    },
    6: {
      title: 'Seller Account Auditing – Required Details',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Details needed for Seller Account Auditing:</h3>
            <ul className="list-disc pl-5 text-gray-600 space-y-1">
              <li>Seller Account Read Access</li>
              <li>Account Health Metrics</li>
              <li>Product Listing Details</li>
              <li>Sales & Traffic Reports</li>
              <li>Inventory Status</li>
              <li>Ad Performance Reports</li>
            </ul>
          </div>
        </div>
      )
    },
    7: {
      title: 'Image Creation – Required Details',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Details needed for Image Creation:</h3>
            <ul className="list-disc pl-5 text-gray-600 space-y-1">
              <li>High-Resolution Product Photos / Raw Images</li>
              <li>Product Dimensions & Key Features</li>
              <li>Brand Logo File (PNG Preferred)</li>
              <li>Reference / Competitor Image Style</li>
              <li>Packaging or Label Details</li>
              <li>Target Platform Guidelines (Amazon, Flipkart, etc.)</li>
            </ul>
          </div>
        </div>
      )
    }
  };

  const getServiceTypeId = (serviceId) => {
    const service = servicesData.find(s => s.id === serviceId);
    return service ? service.serviceTypeId : 0;
  };

  const getServiceTypeName = (serviceId) => {
    const service = servicesData.find(s => s.id === serviceId);
    return service ? service.serviceType : "General Service";
  };

  const handleClickOutside = (event, modalRef, closeFunction) => {
    if (modalRef.current && !modalRef?.current?.contains(event.target)) {
      closeFunction();
    }
  };

  const handleMoreInfoClick = (serviceId) => {
    setSelectedService(serviceId);
    setIsServiceModalOpen(true);
  };

  const handleCloseServiceModal = () => {
    setIsServiceModalOpen(false);
    setSelectedService(null);
  };

  const handleOpenRegistration = () => {
    setIsServiceModalOpen(false);
    setTimeout(() => {
      setIsRegistrationPopupOpen(true);
    }, 50);
  };

  const handleCloseRegistrationPopup = () => {
    setIsRegistrationPopupOpen(false);
  };

  const handleRegistrationSubmit = async (formData) => {
    setIsLoading(true);
    
    try {
      const serviceTypeId = getServiceTypeId(selectedService);
       const phoneNumber = formData.phone || formData.phonenumber;
      const apiData = {
        username: formData.name,
        email: formData.email,
        phonenumber: phoneNumber,
        servicetype: serviceTypeId,
        status: "4",
        currentstatus: "4",
        givenstatus: "4",
        logstatus: "4"
      };

      console.log('API Data:', apiData);
       const result = await postRequest(apiUrls.createserviceregister, apiData);
      console.log('Registration successful - Response:', result);
      setIsRegistrationPopupOpen(false);
      
      const successMessage = result?.message || result?.data?.message;
      
      // Workaround: The backend ignores the status we send and sets it to 1 by default.
      // We will fetch the incomplete services, find the newly created one, and update it to 4.
      try {
        const incompleteServices = await postRequest(apiUrls.getincompleteservice, {});
        if (incompleteServices && incompleteServices.data) {
          const userServices = incompleteServices.data.filter(s => s.email === formData.email && s.phonenumber === phoneNumber);
          if (userServices.length > 0) {
            const newlyCreated = userServices.reduce((prev, current) => (prev.rcid > current.rcid) ? prev : current);
            if (newlyCreated && newlyCreated.rcid) {
               await postRequest(apiUrls.updateserviceregister, {
                 rcid: newlyCreated.rcid,
                 givenstatus: 4,
                 description: 'Initial registration status updated to New'
               });
               console.log('Successfully updated status to 4 via workaround');
            }
          }
        }
      } catch (workaroundError) {
         console.error('Workaround to update status failed:', workaroundError);
      }
      
        toast.success(successMessage || 'Registration successful!', {});
      
    } catch (error) {
      console.error('Error details:', error);
      let errorMsg = 'Registration failed. ';
      
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.data?.msg) {
        errorMsg = error.response.data.msg;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg, { });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenMessagePopup = () => {
    setIsMessagePopupOpen(true);
  };

  const handleCloseMessagePopup = () => {
    setIsMessagePopupOpen(false);
    setMessageFormData({ name: '', email: '', phonenumber: '', question: '' });
  };

  const handleMessageChange = (e) => {
    const { name, value } = e.target;
    setMessageFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const messageApiData = {
        name: messageFormData.name,
        email: messageFormData.email,
        phonenumber: messageFormData.phonenumber,
        question: messageFormData.question,
      };
      
      console.log('Sending message:', messageApiData);
      setIsMessagePopupOpen(false);
      setMessageFormData({ name: '', email: '', phonenumber: '', question: '' });
      
      toast.success('📧 Message sent successfully! We will get back to you soon.', { });
      
    } catch (error) {
      console.error('Message error:', error);
      
      let errorMsg = error.response?.data?.message || error.message || 'Failed to send message. Please try again.';
      
      toast.error(errorMsg, {});
    } finally {
      setIsLoading(false);
    }
  };

  
  useEffect(() => {
    const handleEsc = (e) => {
      if (e?.key === 'Escape') {
        if (isServiceModalOpen) handleCloseServiceModal();
        if (isRegistrationPopupOpen) handleCloseRegistrationPopup();
        if (isMessagePopupOpen) handleCloseMessagePopup();
      }
    };

    const handleClickOutsideService = (e) => {
      if (isServiceModalOpen) {
        handleClickOutside(e, serviceModalRef, handleCloseServiceModal);
      }
    };

    const handleClickOutsideRegistration = (e) => {
      if (isRegistrationPopupOpen) {
        handleClickOutside(e, registrationPopupRef, handleCloseRegistrationPopup);
      }
    };

    const handleClickOutsideMessage = (e) => {
      if (isMessagePopupOpen) {
        handleClickOutside(e, messagePopupRef, handleCloseMessagePopup);
      }
    };

    window.addEventListener('keydown', handleEsc);
    document.addEventListener('mousedown', handleClickOutsideService);
    document.addEventListener('mousedown', handleClickOutsideRegistration);
    document.addEventListener('mousedown', handleClickOutsideMessage);

    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.removeEventListener('mousedown', handleClickOutsideService);
      document.removeEventListener('mousedown', handleClickOutsideRegistration);
      document.removeEventListener('mousedown', handleClickOutsideMessage);
    };
  }, [isServiceModalOpen, isRegistrationPopupOpen, isMessagePopupOpen]);

  return (
    <>
      <section id="services" className="py-16 bg-white relative">
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-200 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-100 text-xs font-bold text-orange-600 tracking-wider uppercase mb-4">
              <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              What We Offer
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight">
              Our <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="max-w-2xl mx-auto text-gray-600 text-base md:text-lg leading-relaxed">
              We don't just teach. We do. Our expert team is ready to handle the heavy lifting for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {servicesData.map((service) => (
              <ServiceCard
                key={service?.id}
                id={service?.id}
                image={service?.image}
                title={service?.title}
                description={service?.description}
                price={service?.price}
                onMoreInfoClick={handleMoreInfoClick}
              />
            ))}

            <div className="bg-gradient-to-br from-amber-600 to-orange-600 p-6 rounded-3xl transition-all duration-300 hover:-translate-y-1 flex flex-col justify-center items-center text-center text-white">
              <h3 className="text-lg font-medium mb-4">Talk to Us</h3>
              <p className="text-amber-100 text-sm mb-8 font-medium">
                Let's build a tailored strategy for your specific business needs.
              </p>
              <button
                onClick={handleOpenMessagePopup}
                className="bg-white text-amber-600 px-6 py-3 rounded-xl font-semibold text-sm hover:bg-gray-300 transition-colors"
              >
                Messages
              </button>
            </div>
          </div>
        </div>
      </section>

      <ServicePopup
        isOpen={isServiceModalOpen}
        serviceId={selectedService}
        serviceDetails={serviceDetails}
        onClose={handleCloseServiceModal}
        onRegister={handleOpenRegistration}
      />

      <RegistrationPopup
        isOpen={isRegistrationPopupOpen}
        onClose={handleCloseRegistrationPopup}
        onSubmit={handleRegistrationSubmit}
        isLoading={isLoading}
        selectedService={selectedService ? servicesData.find(s => s.id === selectedService) : null}
      />

      <MessagePopup
        isOpen={isMessagePopupOpen}
        formData={messageFormData}
        onClose={handleCloseMessagePopup}
        onChange={handleMessageChange}
        onSubmit={handleMessageSubmit}
        isLoading={isLoading}
      />
      {/* Service Logs Popup */}
      <ServiceLogsPopup
        isOpen={isServiceLogsOpen}
        onClose={() => setIsServiceLogsOpen(false)}
        logs={logData}
        isLoading={false}
        serviceName={selectedService ? servicesData.find(s => s.id === selectedService).title : ''}
        userName={currentUserName}
      />
    </>
  );
}